import type { PlantSymbol } from "@garden/types/db";
import { PIN_SIZES } from "./MapPin";
import "./SymbolPicker.css";

interface SymbolPickerProps {
  value: PlantSymbol | null;
  onChange: (symbol: PlantSymbol | null) => void;
}

interface SymbolDef {
  symbol: PlantSymbol;
  label: string;
}

const CATEGORIES: ReadonlyArray<{ label: string; items: ReadonlyArray<SymbolDef> }> = [
  {
    label: "Trees",
    items: [
      { symbol: "tree", label: "Tree" },
      { symbol: "tree_conifer", label: "Conifer" },
      { symbol: "tree_fruit", label: "Fruit tree" },
    ],
  },
  {
    label: "Shrubs",
    items: [
      { symbol: "shrub", label: "Shrub" },
      { symbol: "shrub_topiary", label: "Topiary" },
      { symbol: "bamboo", label: "Bamboo" },
    ],
  },
  {
    label: "Flowers",
    items: [
      { symbol: "rose", label: "Rose" },
      { symbol: "daisy", label: "Daisy" },
      { symbol: "tulip", label: "Tulip" },
      { symbol: "sunflower", label: "Sunflower" },
      { symbol: "lavender", label: "Lavender" },
      { symbol: "foxglove", label: "Foxglove" },
    ],
  },
  {
    label: "Climbers",
    items: [
      { symbol: "vine", label: "Vine" },
      { symbol: "climber", label: "Climber" },
    ],
  },
  {
    label: "Edibles",
    items: [
      { symbol: "veg", label: "Veg" },
      { symbol: "carrot", label: "Carrot" },
      { symbol: "tomato", label: "Tomato" },
      { symbol: "herb", label: "Herb" },
    ],
  },
  {
    label: "Other",
    items: [
      { symbol: "fern", label: "Fern" },
      { symbol: "container", label: "Container" },
    ],
  },
];

const TILE = 56; // px square button
const ICON_SIZE = 36; // visible pin size inside the button

function spriteId(symbol: PlantSymbol): string {
  return symbol.charAt(0).toUpperCase() + symbol.slice(1);
}

export function SymbolPicker({ value, onChange }: SymbolPickerProps) {
  return (
    <div className="SymbolPicker">
      {CATEGORIES.map((cat) => (
        <fieldset key={cat.label} className="SymbolPicker__group">
          <legend className="SymbolPicker__legend">{cat.label}</legend>
          <div className="SymbolPicker__grid">
            {cat.items.map((item) => {
              const selected = value === item.symbol;
              const aspect = PIN_SIZES[item.symbol];
              const scale = ICON_SIZE / Math.max(aspect.w, aspect.h);
              const renderW = aspect.w * scale;
              const renderH = aspect.h * scale;
              return (
                <button
                  key={item.symbol}
                  type="button"
                  className={`SymbolPicker__tile${selected ? " SymbolPicker__tile--selected" : ""}`}
                  aria-pressed={selected}
                  title={item.label}
                  onClick={() => onChange(selected ? null : item.symbol)}
                >
                  <svg
                    width={TILE - 18}
                    height={TILE - 18}
                    viewBox={`-${(TILE - 18) / 2} -${(TILE - 18) / 2} ${TILE - 18} ${TILE - 18}`}
                  >
                    <use href={`#mp${spriteId(item.symbol)}`} x={-renderW / 2} y={-renderH / 2} width={renderW} height={renderH} />
                  </svg>
                  <span className="SymbolPicker__label">{item.label}</span>
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
